import { ng } from "@common/ngds";
import { GraphDirection } from "./SideView";
import { Drawer, StyleForRect } from "@common/components/Rendering";
import { center_line_style, ArrowId } from "../ViewportStyles";
import { calculateGeomCrossSection } from "./drawGeomCrossSection";

interface ColumnSize {
  size: number;
  height: number;
}

export const calculate_column_side_geom_size = (
  projectData: ng.frontend.column.Project | null,
  state: GraphDirection,
): ColumnSize => {
  const structure = projectData?.data?.input?.structures?.[0];
  const first_element = structure?.elements?.[0];
  const first_segment = first_element?.getBeam()?.segments?.[0];
  const secGuid = first_segment?.layer?.start?.secGuid;
  const sec = projectData?.data?.input?.secDb.find((x) => x.id?.guid === secGuid);
  const params = sec?.params;

  // in meters
  const column_size: number = (() => {
    const pt = params?.type;
    const raw = (() => {
      switch (pt) {
        case "rParams":
          return state === "xy" ? params.rParams.width : params.rParams.height;
        case "sParams":
          return params.sParams.side;
        case "cParams":
          return params.cParams.diameter;
        default:
          if (params === null) {
            // for steel and timber there are no params in the database
            // solve this by calculating the extent from the polyline
            const extent = projectData ? calculateGeomCrossSection(projectData) : null;
            if (extent) {
              return state === "xy" ? extent.hor.max - extent.hor.min : extent.ver.max - extent.ver.min;
            }
          }
          console.log(`Unhandled param type ${pt} / ${params}`);
          return 0;
      }
    })();
    return Number.isFinite(raw) ? raw : 0;
  })();

  const beam = first_element?.getBeam();
  const nodes = structure?.nodes ?? [];
  const startNode = nodes.find((n) => n.id?.guid === beam?.startNodeGuid);
  const endNode = nodes.find((n) => n.id?.guid === beam?.endNodeGuid);
  const dx = (endNode?.coord?.x ?? 0) - (startNode?.coord?.x ?? 0);
  const dy = (endNode?.coord?.y ?? 0) - (startNode?.coord?.y ?? 0);
  const dz = (endNode?.coord?.z ?? 0) - (startNode?.coord?.z ?? 0);
  const fallbackLength = Math.sqrt(dx * dx + dy * dy + dz * dz);

  const rawHeight = first_segment?.length ?? 0;
  const column_height = Number.isFinite(rawHeight) && rawHeight > 0 ? rawHeight : fallbackLength;

  return { size: column_size, height: column_height };
};

export const drawGeometry = (
  g: Drawer<ArrowId>,
  sett: {
    center_line_hor: number;
    column_size: number;
    column_height: number;
    is_visible: boolean;
    geom_style: StyleForRect;
  },
) => {
  const { center_line_hor, column_size, column_height } = sett;
  // column geom
  g.drawRect([center_line_hor - column_size / 2, -column_height], column_size, column_height, sett.geom_style, {
    display: sett.is_visible,
  });

  // center line
  g.drawLine([center_line_hor, -column_height], [center_line_hor, 0], center_line_style, {
    display: sett.is_visible,
  });
};
